import axios from 'axios'
import type { 
  ScanRequest, 
  ScanResponse, 
  TestResult,
  Report,
  BatchScanRequest,
  BatchScanResponse,
  ExportOptions,
  ApiResponse
} from '../../../shared/types'

// Use relative path so Vite proxy works correctly
const API_BASE_URL = '/api/v1'

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
})

// Add auth token to requests if available
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('accessToken')
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }
  return config
})

// Handle API errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('accessToken')
      window.location.href = '/login'
    }
    return Promise.reject(error)
  }
)

// Single Scan APIs
export const scanUrl = async (data: ScanRequest): Promise<ScanResponse> => {
  const response = await api.post<ApiResponse<ScanResponse>>('/scan', data)
  if (!response.data.success) {
    throw new Error(response.data.error?.message || 'Scan failed')
  }
  return response.data.data!
}

export const getScanResult = async (scanId: string): Promise<ScanResponse> => {
  const response = await api.get<ApiResponse<ScanResponse>>(`/scan/${scanId}`)
  if (!response.data.success) {
    throw new Error(response.data.error?.message || 'Failed to get scan result')
  }
  return response.data.data!
}

export const getTestResult = async (scanId: string): Promise<TestResult> => {
  const response = await api.get<ApiResponse<TestResult>>(`/scan/${scanId}/result`)
  if (!response.data.success) {
    throw new Error(response.data.error?.message || 'Failed to get test result')
  }
  return response.data.data!
}

// Batch Scan APIs
export const startBatchScan = async (data: BatchScanRequest): Promise<BatchScanResponse> => {
  const response = await api.post<ApiResponse<BatchScanResponse>>('/batch', data)
  if (!response.data.success) {
    throw new Error(response.data.error?.message || 'Batch scan failed')
  }
  return response.data.data!
}

export const getBatchStatus = async (batchId: string): Promise<BatchScanResponse> => {
  const response = await api.get<ApiResponse<BatchScanResponse>>(`/batch/${batchId}`)
  if (!response.data.success) {
    throw new Error(response.data.error?.message || 'Failed to get batch status')
  }
  return response.data.data!
}

// Report APIs
export const getReports = async (params?: {
  page?: number
  limit?: number
  sort?: string
}): Promise<{ reports: Report[]; total: number }> => {
  const response = await api.get<ApiResponse<{ reports: Report[]; total: number }>>('/reports', { params })
  if (!response.data.success) {
    throw new Error(response.data.error?.message || 'Failed to get reports')
  }
  return response.data.data!
}

export const getReport = async (reportId: string): Promise<Report> => {
  const response = await api.get<ApiResponse<Report>>(`/reports/${reportId}`)
  if (!response.data.success) {
    throw new Error(response.data.error?.message || 'Failed to get report')
  }
  return response.data.data!
}

export const exportReport = async (
  reportId: string, 
  options: ExportOptions
): Promise<Blob> => {
  const response = await api.post(
    `/reports/${reportId}/export`,
    options,
    { responseType: 'blob' }
  )
  return response.data
}

// Dashboard APIs
export const getDashboardStats = async (): Promise<{
  totalScans: number
  averageScore: number
  criticalIssues: number
  recentScans: ScanResponse[]
  trendData: Array<{
    date: string
    score: number
    errors: number
    warnings: number
  }>
}> => {
  const response = await api.get<ApiResponse<any>>('/dashboard/stats')
  if (!response.data.success) {
    throw new Error(response.data.error?.message || 'Failed to get dashboard stats')
  }
  return response.data.data!
}

// Settings APIs
export const getSettings = async (): Promise<{
  defaultWcagLevel: 'A' | 'AA' | 'AAA'
  customRules: string[]
  notifications: boolean
  apiKey?: string
}> => {
  const response = await api.get<ApiResponse<any>>('/settings')
  if (!response.data.success) {
    throw new Error(response.data.error?.message || 'Failed to get settings')
  }
  return response.data.data!
}

export const updateSettings = async (settings: any): Promise<void> => {
  const response = await api.put<ApiResponse<void>>('/settings', settings)
  if (!response.data.success) {
    throw new Error(response.data.error?.message || 'Failed to update settings')
  }
}

// Health Check
export const checkHealth = async (): Promise<{ status: string; version: string }> => {
  const response = await api.get<{ status: string; version: string }>('/health')
  return response.data
}